/**
 * FileUpload - Professional drag-drop file upload component
 * 
 * Features:
 * - Modern drag & drop interface
 * - File type validation 
 * - Image previews
 * - Progress indicators
 * - Mobile-friendly
 * - Comprehensive error handling
 */

import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, Image, AlertCircle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface FileUploadProps {
  onFileUploaded: (attachment: any) => void;
  conversationId: string;
  uploadedBy?: string;
  maxSize?: number; // in bytes
  allowedTypes?: string[];
  multiple?: boolean;
  disabled?: boolean;
  compact?: boolean; // For chat widget usage
}

interface FileUploadState {
  isDragOver: boolean;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  preview: string | null;
}

const DEFAULT_ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf', 'text/plain', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileUploaded,
  conversationId,
  uploadedBy = 'anonymous',
  maxSize = MAX_FILE_SIZE,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  multiple = false,
  disabled = false,
  compact = false
}) => {
  const [state, setState] = useState<FileUploadState>({
    isDragOver: false,
    isUploading: false,
    uploadProgress: 0,
    error: null,
    preview: null
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Validate file before upload
  const validateFile = (file: File): string | null => {
    // Size check
    if (file.size > maxSize) {
      return `File too large. Maximum size is ${(maxSize / (1024 * 1024)).toFixed(1)}MB`;
    }

    // Type check
    if (!allowedTypes.includes(file.type)) {
      return `File type '${file.type}' is not supported`;
    }

    return null;
  };

  // Generate file preview for images
  const generatePreview = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  // Upload file to server
  const uploadFile = async (file: File) => {
    setState(prev => ({ ...prev, isUploading: true, error: null, uploadProgress: 0 }));

    try {
      // Generate preview for images
      const preview = await generatePreview(file);
      setState(prev => ({ ...prev, preview }));

      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversationId', conversationId);
      formData.append('uploadedBy', uploadedBy);

      console.log('[FILE-UPLOAD] 🚀 Starting upload:', {
        filename: file.name,
        size: file.size,
        type: file.type,
        conversationId
      });

      const response = await fetch('https://iwishbag-chatbot.rnkbohra.workers.dev/api/upload', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      console.log('[FILE-UPLOAD] ✅ Upload successful:', result.attachment);

      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        uploadProgress: 100,
        error: null 
      }));

      // Call success callback
      onFileUploaded(result.attachment);

      // Reset state after brief success indication
      setTimeout(() => {
        setState(prev => ({ 
          ...prev, 
          uploadProgress: 0, 
          preview: null 
        }));
      }, 2000);

    } catch (error) {
      console.error('[FILE-UPLOAD] ❌ Upload failed:', error);
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        error: (error as Error).message,
        uploadProgress: 0,
        preview: null
      }));
    }
  };

  // Handle file selection
  const handleFiles = useCallback(async (files: FileList) => {
    if (disabled || state.isUploading) return;

    const file = files[0]; // Handle single file for now
    if (!file) return;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setState(prev => ({ ...prev, error: validationError }));
      return;
    }

    // Upload file
    await uploadFile(file);
  }, [disabled, state.isUploading]);

  // Drag & Drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !state.isUploading) {
      setState(prev => ({ ...prev, isDragOver: true }));
    }
  }, [disabled, state.isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide drag over if leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setState(prev => ({ ...prev, isDragOver: false }));
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState(prev => ({ ...prev, isDragOver: false }));

    if (disabled || state.isUploading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, state.isUploading, handleFiles]);

  // File input change handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    // Reset input value
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    if (!disabled && !state.isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Clear error
  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  return (
    <div className="w-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={allowedTypes.join(',')}
        multiple={multiple}
        onChange={handleFileInputChange}
        disabled={disabled}
      />

      {/* Drop Zone */}
      <div
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileInput}
        className={`
          relative border-2 border-dashed rounded-lg cursor-pointer
          transition-all duration-200 ease-in-out
          ${compact ? 'p-3' : 'p-6'}
          ${compact ? 'max-h-32' : ''}
          ${state.isDragOver 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${state.isUploading ? 'pointer-events-none' : ''}
        `}
      >
        {/* Upload Content */}
        <div className={`flex flex-col items-center justify-center ${compact ? 'space-y-2' : 'space-y-3'}`}>
          
          {/* Upload Icon/Preview */}
          {state.preview ? (
            <div className="relative">
              <img 
                src={state.preview} 
                alt="Preview" 
                className="w-20 h-20 object-cover rounded-lg border"
              />
              {state.isUploading && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
          ) : (
            <div className={`
              ${compact ? 'w-10 h-10' : 'w-16 h-16'} rounded-full flex items-center justify-center
              ${state.isUploading 
                ? 'bg-blue-100' 
                : state.isDragOver 
                  ? 'bg-blue-100' 
                  : 'bg-gray-200'
              }
            `}>
              {state.isUploading ? (
                <Loader2 className={`${compact ? 'w-4 h-4' : 'w-8 h-8'} text-blue-600 animate-spin`} />
              ) : state.uploadProgress === 100 ? (
                <Check className={`${compact ? 'w-4 h-4' : 'w-8 h-8'} text-green-600`} />
              ) : (
                <Upload className={`
                  ${compact ? 'w-4 h-4' : 'w-8 h-8'} 
                  ${state.isDragOver ? 'text-blue-600' : 'text-gray-600'}
                `} />
              )}
            </div>
          )}

          {/* Upload Text */}
          <div className="text-center">
            {state.isUploading ? (
              <div>
                <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-blue-600`}>Uploading...</p>
                {!compact && <p className="text-xs text-gray-500 mt-1">Please wait</p>}
              </div>
            ) : state.uploadProgress === 100 ? (
              <div>
                <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-green-600`}>Upload Complete!</p>
                {!compact && <p className="text-xs text-gray-500 mt-1">File uploaded successfully</p>}
              </div>
            ) : (
              <div>
                <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>
                  {state.isDragOver ? 'Drop file here' : compact ? 'Click or drag to upload' : 'Click to upload or drag and drop'}
                </p>
                {!compact && (
                  <p className="text-xs text-gray-500 mt-1">
                    Images, PDFs, Documents (max {(maxSize / (1024 * 1024)).toFixed(1)}MB)
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Supported File Types */}
          {!state.isUploading && state.uploadProgress === 0 && !compact && (
            <div className="flex flex-wrap gap-1 justify-center">
              <Badge variant="outline" className="text-xs">JPG</Badge>
              <Badge variant="outline" className="text-xs">PNG</Badge>
              <Badge variant="outline" className="text-xs">PDF</Badge>
              <Badge variant="outline" className="text-xs">DOC</Badge>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {state.isUploading && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-300 ease-out"
              style={{ width: `${state.uploadProgress}%` }}
            />
          </div>
        )}
      </div>

      {/* Error Message */}
      {state.error && (
        <div className="mt-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{state.error}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearError}
            className="h-6 w-6 p-0 hover:bg-red-100"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Mobile Upload Button */}
      {!compact && (
        <div className="mt-3 sm:hidden">
          <Button 
            onClick={triggerFileInput}
            disabled={disabled || state.isUploading}
            className="w-full"
            variant="outline"
          >
            {state.isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Choose File
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;